/**
 * EULA Consent Modal
 *
 * First-launch (and on EULA bumps) acceptance modal. Required by Apple
 * App Store Guideline 1.2 and 3.1.2(a) for any app with user-generated
 * content. The accept action calls `POST /api/users/me/accept-eula`,
 * which persists `eula_version` + `eula_accepted_at` onto the user
 * document for compliance audits.
 *
 * The modal blocks the rest of the app (z-300 + non-dismissable backdrop)
 * until the user either accepts or signs out.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Scroll, Lock, ArrowSquareOut } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
export const EULA_CURRENT_VERSION = '2026-02-21';

export default function EulaConsentModal({ open, onAccept, onDecline }) {
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;

  const accept = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/users/me/accept-eula`,
        { version: EULA_CURRENT_VERSION },
        { withCredentials: true }
      );
      try { localStorage.setItem('cyb_eula_version', data.eula_version); } catch { /* ignore */ }
      onAccept?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md" data-testid="eula-consent-modal">
      <div className="w-full max-w-md bg-[#0c0c14] border border-white/10 rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#00C8D7] flex items-center justify-center">
            <ShieldCheck size={22} weight="duotone" className="text-white" />
          </div>
          <div>
            <h2 className="text-white text-base font-bold leading-tight" style={{ fontFamily: 'Unbounded' }}>
              Welcome to CyBoard
            </h2>
            <p className="text-[#888] text-[11px] mt-0.5">Quick agreement before you start playing</p>
          </div>
        </div>

        <p className="text-[#ccc] text-[12px] leading-relaxed mb-3">
          Before using CyBoard, please review and accept our Terms of Service and Privacy Policy.
          Key points you&apos;re agreeing to:
        </p>

        <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 mb-3 space-y-1.5">
          <Bullet>You&apos;re at least 13 years old (16 in the EEA).</Bullet>
          <Bullet>You will <span className="text-white font-medium">not</span> post objectionable content, harass, threaten, or impersonate other users. Reports are reviewed within 24 hours and offenders are removed.</Bullet>
          <Bullet>CyBoard organizes <span className="text-white font-medium">in-person</span> meetups. Use common sense — meet in public places when first connecting with strangers.</Bullet>
          <Bullet>Your public profile (name, username, city, badges, public match history) is visible to other players.</Bullet>
          <Bullet>Optional AI features send your prompts (and, for Board Scan, photos) to OpenAI for processing. You&apos;ll be asked separately before any AI call.</Bullet>
        </div>

        <div className="flex gap-2 mb-4 text-[11px]">
          <a href="/terms" target="_blank" rel="noreferrer"
             className="flex-1 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.07] text-[#00C8D7] flex items-center justify-center gap-1.5 transition-colors"
             data-testid="eula-open-terms">
            <Scroll size={13} weight="duotone" /> Read Terms <ArrowSquareOut size={11} />
          </a>
          <a href="/privacy" target="_blank" rel="noreferrer"
             className="flex-1 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.07] text-[#9D4CDD] flex items-center justify-center gap-1.5 transition-colors"
             data-testid="eula-open-privacy">
            <Lock size={13} weight="duotone" /> Privacy <ArrowSquareOut size={11} />
          </a>
        </div>

        <p className="text-[10px] text-[#666] mb-3 leading-snug">
          By tapping &quot;Agree &amp; Continue&quot; you confirm you&apos;ve read and accept the Terms of Service
          and Privacy Policy (version {EULA_CURRENT_VERSION}). We&apos;ll save your acceptance on your account.
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={accept}
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#7B2CBF] text-white text-[13px] font-semibold disabled:opacity-60 transition-all"
            data-testid="eula-accept-btn"
          >
            {submitting ? 'Saving…' : 'Agree & Continue'}
          </button>
          <button
            onClick={onDecline}
            disabled={submitting}
            className="w-full py-2 text-[#71717A] hover:text-white text-[11px] font-medium transition-colors"
            data-testid="eula-decline-btn"
          >
            I do not agree — sign me out
          </button>
        </div>
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#9D4CDD] text-[12px] mt-0.5 flex-shrink-0">•</span>
      <p className="text-[11px] text-[#ccc] leading-relaxed">{children}</p>
    </div>
  );
}
